import { provideHttpClient, withInterceptors } from '@angular/common/http';
import type { EnvironmentProviders, Provider, Type } from '@angular/core';
import { importProvidersFrom, inject, provideAppInitializer, provideEnvironmentInitializer } from '@angular/core';
import { MATERIAL_SANITY_CHECKS } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { mockApiInterceptor } from '@spri/lib/mock-api';
import type { SpriConfig } from '@spri/services/config';
import { spri_CONFIG } from '@spri/services/config/config.constants';
import { spriConfirmationService } from '@spri/services/confirmation';
import { spriLoadingService } from '@spri/services/loading';
import { SpriMediaWatcherService } from '@spri/services/media-watcher';
import { SpriPlatformService } from '@spri/services/platform';
import { SpriScreenSizeService } from '@spri/services/screen-size';
import { SpriSplashScreenService } from '@spri/services/splash-screen';
import { SpriUtilsService } from '@spri/services/utils';

export interface spriProviderConfig {
  mockApi?: {
    delay?: number;
    service?: Type<unknown>;
  };
  spri?: SpriConfig;
}

/**
 * spri provider
 */
export const provideSpri = (config: spriProviderConfig): (Provider | EnvironmentProviders)[] => {
  // Base providers
  const providers: (Provider | EnvironmentProviders)[] = [
    {
      // Disable 'theme' sanity check
      provide: MATERIAL_SANITY_CHECKS,
      useValue: {
        doctype: true,
        theme: false,
        version: true,
      },
    },
    {
      // Use the 'fill' appearance on Angular Material form fields by default
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'fill',
      },
    },
    {
      provide: spri_CONFIG,
      useValue: config?.spri ?? {},
    },

    importProvidersFrom(MatDialogModule),
    provideEnvironmentInitializer(() => inject(spriConfirmationService)),

    provideEnvironmentInitializer(() => inject(spriLoadingService)),

    provideEnvironmentInitializer(() => inject(SpriMediaWatcherService)),
    provideEnvironmentInitializer(() => inject(SpriPlatformService)),
    provideEnvironmentInitializer(() => inject(SpriScreenSizeService)),
    provideEnvironmentInitializer(() => inject(SpriSplashScreenService)),
    provideEnvironmentInitializer(() => inject(SpriUtilsService)),
  ];

  // Mock Api services
  if (config?.mockApi?.service) {
    providers.push(
      provideHttpClient(withInterceptors([mockApiInterceptor])),
      provideAppInitializer(() => {
        inject(config.mockApi.service);
      })
    );
  }

  // Return the providers
  return providers;
};
